import { Roles } from '../constants'
import { nonSelectedEstate } from '../reducers/app'

const EstateUtils = {
    /**
     * Gets the current state ID
     */
    getCurrentEstateId(state) {
        // Redux reducer name
        const RESOURCE_REDUCER = 'app'
        // Selected estate variable name
        const RESOURCE_SELECTED = 'selectedEstate'

        // Gets data from store
        const {
            [RESOURCE_REDUCER]: { [RESOURCE_SELECTED]: selected },
        } = state

        // No estate has been selected
        if (selected === nonSelectedEstate) {
            return null
        }

        return selected
    },

    /**
     * Get estate data
     * @param {String} state Current state
     * @param {String} [estateId] Estate ID to look for. It uses selected estate by default
     * @returns Estate data
     */
    getEstateData(state, estateId = null) {
        // Redux reducer name
        const RESOURCE_REDUCER = 'app'
        // Estate array name on store
        const RESOURCE_NAME = 'estates'

        // Gets data from store
        const {
            [RESOURCE_REDUCER]: { [RESOURCE_NAME]: resources },
        } = state

        // Cannot find estate array
        if (!resources) {
            throw new Error(`Cannot find resource '${RESOURCE_NAME}' inside '${RESOURCE_REDUCER}' reducer`)
        }

        // Get Current Estate ID
        const selected = estateId || EstateUtils.getCurrentEstateId(state)

        // Try to find selected estate
        const estate = resources.find(({ id }) => id === selected)

        // Cannot find selected estate data
        if (!estate) {
            return null
        }

        return estate
    },

    /**
     * Gets the API url from the selected estate on redux store
     */
    getAPI(state) {
        // Try to find selected estate
        const estate = EstateUtils.getEstateData(state)

        const { url } = estate || {}

        // Estate does not have url field
        if (!url) {
            return null
        }

        return url
    },

    /**
     * Gets the Socket Server URL
     */
    getSocketServer(state) {
        // Try to find selected estate
        const estate = EstateUtils.getEstateData(state)

        const { socketServer } = estate || {}

        // Estate does not have socket server url
        if (!socketServer) {
            return null
        }

        return socketServer
    },

    /**
     *
     * @param {Object} state
     * @deprecated
     */
    getBucketInfo(state) {
        // Try to find selected estate
        const estate = EstateUtils.getEstateData(state)

        const { bucketLayers, bucketMedia, bucketKey, bucketSecret } = estate

        // Estate does not have bucket name
        if (!bucketMedia || !bucketLayers) {
            throw new Error('Undefined bucket on selected estate')
        }

        return { bucketLayers, bucketMedia, bucketKey, bucketSecret }
    },

    /**
     * Check if the user has admin role in current estate
     * @param {Object} state Current state
     * @param {Object} opts Options
     * @param {String} [opts.estate] Estate ID where to check permission.
     * It searches on selected estate if this option is missing.
     * @returns {Boolean} Whether the user is an admin
     */
    isAdmin(state, opts = {}) {
        const { estate: estateId } = opts

        // Try to find selected estate
        const estate = EstateUtils.getEstateData(state, estateId)

        const { isAdmin } = estate || {}

        return isAdmin || EstateUtils.checkRole(state, Roles.ROLE_ADMIN, { estate: estateId })
    },

    /**
     * Checks if a estate id is valid. It expects an uppercase string with three characters
     * @param {String} estate
     */
    isValidEstate(estate = '') {
        return !!estate.match(/^[A-Z0-9]{3}$/)
    },

    /**
     * Check if the user has an acl in current estate
     * @param {Object} state
     * @param {String} acl
     * @param {Object} opts Options
     * @param {String} [opts.estate] Estate ID where to check permission.
     * It searches on selected estate if this option is missing.
     */
    checkACL(state, acl, opts = {}) {
        const { estate: estateId } = opts

        // Try to find selected estate
        const estate = EstateUtils.getEstateData(state, estateId)

        const { acl: aclList = [] } = estate || {}

        return aclList.includes(acl)
    },

    /**
     * Check if the user has an acl in current estate
     * @param {Object} state
     * @param {String} acl
     */
    checkRole(state, role, opts = {}) {
        const { estate: estateId } = opts

        // Try to find selected estate
        const estate = EstateUtils.getEstateData(state, estateId)

        const { roles = [] } = estate || {}

        return roles.includes(role)
    },

    /**
     * Check if the user is admin or if the user has the acl
     * @param {Object} state
     * @param {Object} items
     * @param {Array<String>} [items.acls] List of acls that satisfies the permission
     * @param {Array<String>} [items.roles] List of roles that satisfies the permission
     * @param {Object} opts Options
     * @param {Boolean} [opts.ignoreAdmin] Check only acl and not admin flag
     * @param {String} [opts.estate] Estate ID where to check permission.
     * It searches on selected estate if this option is missing.
     */
    hasPermission(state, { acls = [], roles = [] }, opts = {}) {
        const { estate, ignoreAdmin = false } = opts

        return (
            (acls.length === 0 && roles.length === 0) || // Both acls and roles lists are empty
            (!ignoreAdmin && EstateUtils.isAdmin(state, { estate })) || // User is admin in the estate (is this option is not disabled)
            acls.some((acl) => EstateUtils.checkACL(state, acl, { estate })) || // User has any mandatory acl
            roles.some((role) => EstateUtils.checkRole(state, role, { estate })) // User has any mandatory role
        )
    },

    /**
     * Returns true when the provided estate should behave like the "preprod"
     * estate. This is used to softly rollout features in a single estate
     * without exposing them to others.
     *
     * The check happens in this order:
     *  1) If REACT_APP_PREPROD_ESTATE_IDS is provided (comma separated), the
     *     current estate id must match one of those values (case insensitive).
     *  2) Fallback: estate id or estate name contains the string "preprod"
     *     (case insensitive).
     *
     * @param {Object} state Current redux state
     * @param {Object} opts
     * @param {String} [opts.estate] Estate ID to check. Defaults to selected estate
     * @returns {Boolean}
     */
    isPreprodEstate(state, opts = {}) {
        const { estate: estateId } = opts
        const estateData = EstateUtils.getEstateData(state, estateId)
        const envList = (process.env.REACT_APP_PREPROD_ESTATE_IDS || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
            .map((id) => id.toUpperCase())

        const currentId = (estateData?.id || estateId || '').toUpperCase()

        return envList.length > 0 && currentId
            ? envList.includes(currentId)
            : false
    },

    /**
     * Returns true when the estate is considered staging.
     * Controlled via REACT_APP_STAGING_ESTATE_IDS (comma separated, case-insensitive).
     */
    isStagingEstate(state, opts = {}) {
        const { estate: estateId } = opts
        const estateData = EstateUtils.getEstateData(state, estateId)
        const envList = (process.env.REACT_APP_STAGING_ESTATE_IDS || '')
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean)
            .map((id) => id.toUpperCase())

        const currentId = (estateData?.id || estateId || '').toUpperCase()

        return envList.length > 0 && currentId
            ? envList.includes(currentId)
            : false
    },
}

export default EstateUtils
